import type { NativeIntakeProfileVersion } from '@ohf/core/shared';
import copy from '../copy/en/signing.json' with { type: 'json' };
import type { NativeSignerPayload } from './native-intake.js';
import { createLocalDraftStore, draftFingerprint } from './local-draft.js';

interface Options {
  formKey: string;
  profileVersion: NativeIntakeProfileVersion;
  context: { invitationCode?: string; linkCode?: string; resumeToken?: string; bundleToken?: string };
  baseline: NativeSignerPayload;
  preferServer: boolean;
  signal: AbortSignal;
  panel: HTMLElement;
  canCollect(): boolean;
  collect(): NativeSignerPayload;
  restore(payload: NativeSignerPayload): void;
  getStage(): number;
}

/** Keeps a last-enabled snapshot, so busy fieldsets and unload cannot erase answers. */
export async function createNativeDraftController(options: Options) {
  const store = await createLocalDraftStore(options);
  if (options.signal.aborted) throw new DOMException('Draft initialization cancelled', 'AbortError');
  let baseFingerprint = draftFingerprint(options.baseline);
  let snapshot: { payload: NativeSignerPayload; stage: number } | null = null;
  let persistedFingerprint = '';
  let timer: number | undefined;
  let stopped = false;
  const container = document.createElement('div');
  container.className = 'browser-draft';
  const status = document.createElement('p');
  status.id = 'localDraftStatus'; status.className = 'hint'; status.setAttribute('role', 'status');
  const remove = document.createElement('button');
  remove.id = 'removeLocalDraft'; remove.type = 'button'; remove.className = 'btn btn-ghost btn-small';
  remove.textContent = copy.removeSavedBrowserDraft;
  remove.hidden = true;
  container.append(status, remove); options.panel.append(container);
  const unavailable = () => { status.textContent = copy.browserDraftUnavailable; };
  const saved = store.read();
  let restoredStage: number | null = null;
  if (saved && (!options.preferServer || saved.baseFingerprint === baseFingerprint)) {
    options.restore(saved.payload);
    snapshot = { payload: options.collect(), stage: saved.stage };
    persistedFingerprint = draftFingerprint(snapshot.payload);
    restoredStage = saved.stage;
    status.textContent = copy.browserDraftRestored;
    remove.hidden = false;
  } else {
    if (saved) store.clear();
    status.textContent = copy.browserDraftReady;
  }
  if (!store.available) unavailable();

  const cancelTimer = () => { if (timer !== undefined) window.clearTimeout(timer); timer = undefined; };
  const flush = (): boolean => {
    cancelTimer();
    if (stopped || !snapshot) return false;
    const ok = store.write({ ...snapshot, baseFingerprint });
    if (ok) {
      persistedFingerprint = draftFingerprint(snapshot.payload);
      status.textContent = copy.browserDraftSaved;
      remove.hidden = false;
    } else { persistedFingerprint = ''; unavailable(); }
    return ok;
  };
  const schedule = () => { cancelTimer(); timer = window.setTimeout(flush, 250); };
  const edit = () => {
    if (stopped || !options.canCollect()) return;
    snapshot = { payload: options.collect(), stage: options.getStage() };
    status.textContent = store.available ? copy.browserDraftSaving : copy.browserDraftUnavailable;
    schedule();
  };
  remove.addEventListener('click', () => {
    cancelTimer(); snapshot = null; persistedFingerprint = '';
    if (store.clear()) { status.textContent = store.available ? copy.browserDraftRemoved : copy.browserDraftRemovedUnavailable; remove.hidden = true; }
    else unavailable();
  });
  window.addEventListener('pagehide', flush);
  return {
    restoredStage,
    edit,
    stageChanged() { if (!stopped && snapshot && snapshot.stage !== options.getStage()) { snapshot.stage = options.getStage(); schedule(); } },
    flush,
    isSaved(payload: NativeSignerPayload) { return !!persistedFingerprint && persistedFingerprint === draftFingerprint(payload); },
    reviewed(payload: NativeSignerPayload) {
      baseFingerprint = draftFingerprint(payload);
      if (snapshot) { snapshot.payload = payload; snapshot.stage = options.getStage(); flush(); }
    },
    complete() { stopped = true; cancelTimer(); snapshot = null; persistedFingerprint = ''; store.clear(); container.remove(); },
    dispose() { flush(); stopped = true; cancelTimer(); window.removeEventListener('pagehide', flush); container.remove(); },
  };
}
